import { useState, useEffect, useContext } from "react";
import { RegularButton } from "../../../../common/ReusableComponent/CustomButton";
import AuthContext from "../../../../../utils/secure-route/AuthContext";
import ReusableModal from "../../../AppPAges/ManageSettings/ReusableModal";
import { ReusableInput } from "../../../../common/ReusableComponent/ReusableInput";
import PaginatedSearchSelect from "../../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from "../../../../common/ToastHandler";
import useSwal from "../../../../common/ReusableComponent/useSwal";
import AssetTable from "../../../AssetManagement/AssetsInventory/AssetTable";
import StatusPill from "../../../../common/ReusableComponent/StatusPill";
import ActionButton from "../../../../common/ReusableComponent/ActionButton";
import { FaTrash } from "react-icons/fa";
const thStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    backgroundColor: "#f8f9fa",
    textAlign: "left"
};

const tdStyle = {
    border: "1px solid #ddd",
    padding: "8px"
};


const InventoryQuotation = () => {
    const [showModal, setShowModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [isCreatingFromTable, setIsCreatingFromTable] = useState(false);
    const { user } = useContext(AuthContext);
    const storeId = user.store;
    const { confirmDelete } = useSwal();
    const { handleToast } = useToastHandler();
    const { GetPaginatedData, Post, Delete, Patch, PostDataForm, PatchFormData, getAPI, Get } = useApi();
    const [loading, setLoading] = useState(false);
    const [reminderLoading, setReminderLoading] = useState({});
    const [convertPoLoading, setConvertPoLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [quotationData, setQuotationData] = useState([]);
    const [editingQuotation, setEditingQuotation] = useState(null);
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [quotationFile, setQuotationFile] = useState(null);
    const [showPoItemModal, setShowPoItemModal] = useState(false);
    const [viewRfqId, setViewRfqId] = useState(null);
    const [rfqDetails, setRfqDetails] = useState(null);
    const [createdPoId, setCreatedPoId] = useState(null);
    const [selectedQuotationRow, setSelectedQuotationRow] = useState(null);

    const [formData, setFormData] = useState({
        quotation_number: "",
        rfq_id: "",
        vendor_id: "",
        vendor_name: "",
        vendor_email: "",
        quoted_price: "",
        delivery_time: "",
        notes: "",
        status: "",
        store: storeId,
    });

    const [approvalData, setApprovalData] = useState({
        approval_status: "",
        approval_notes: "",
        approved_by: user.id,
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === "file") {
            setQuotationFile(files[0]);
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
            if (errors[name]) {
                setErrors(prev => ({ ...prev, [name]: "" }));
            }
        }
    };


    const handleApprovalChange = (e) => {
        const { name, value } = e.target;
        setApprovalData(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.rfq_id) newErrors.rfq_id = "RFQ required";
        if (!formData.vendor_id) newErrors.vendor_id = "Vendor required";
        if (!formData.quoted_price) newErrors.quoted_price = "Quoted price required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const fetchQuotationList = async ({ page = 1, searchTerm = "" } = {}) => {
        try {
            setLoading(true);
            const response = await GetPaginatedData("productRfqVendor", {
                page,
                search: searchTerm || undefined,
            });

            const results = response?.data || [];
            setQuotationData(results);
            setTotalCount(response?.totalCount || 0);
        } catch (error) {
            console.error("Fetch error:", error);
            setQuotationData([]);
            handleToast({ type: "ERROR", message: "Failed to fetch quotations" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchQuotationList({ page: currentPage, searchTerm: debouncedSearchTerm });
    }, [currentPage, debouncedSearchTerm, refreshTrigger]);


    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = new FormData();
            payload.append("rfq", formData.rfq_id);
            payload.append("vendor", formData.vendor_id);
            payload.append("vendor_name", formData.vendor_name);
            payload.append("vendor_email", formData.vendor_email || "");
            payload.append("total_quoted_price", formData.quoted_price);
            payload.append("delivery_days", formData.delivery_time);
            payload.append("notes", formData.notes || "");
            payload.append("store", storeId);

            if (quotationFile) {
                payload.append("quotation_file", quotationFile);
            }

            if (editingQuotation) {
                // PATCH for editing existing quotation
                payload.append("status", "quotation_submitted");
                await PatchFormData("productRfqVendor", editingQuotation.id, payload);
                handleToast({ type: "SUCCESS", message: "Quotation updated successfully" });
                setRefreshTrigger(p => p + 1);
            } else {
                // POST for creating new quotation
                await PostDataForm("productRfqVendor", payload);
                handleToast({ type: "SUCCESS", message: "Quotation added successfully" });
            }

            setShowModal(false);
            resetForm();
            setRefreshTrigger(p => p + 1);

        } catch (err) {
            handleToast({ type: "ERROR", message: editingQuotation ? "Failed to update quotation" : "Failed to add quotation" });
        }
    };

    const openQuotationModal = (row) => {
        // Always use PATCH for table rows since they already exist in database
        setEditingQuotation(row);

        setFormData({
            rfq_id: row.rfq,
            rfq_number: row.rfq_number,
            vendor_id: row.vendor,
            vendor_name: row.vendor_name,
            vendor_email: row.vendor_email || "",
            quoted_price: row.total_quoted_price || "",
            delivery_time: row.delivery_days || "",
            notes: row.notes || "",
            store: storeId
        });

        setIsCreatingFromTable(true);
        setShowModal(true);
    };

    const handleApprovalSubmit = async (e) => {
        e.preventDefault();

        try {
            await Patch("productRfqVendor", selectedQuotation.id, {
                status: "approved"
            });

            handleToast({ type: "SUCCESS", message: "Quotation approval updated successfully" });
            setShowApprovalModal(false);
            setSelectedQuotation(null);
            setApprovalData({ approval_status: "", approval_notes: "", approved_by: user.id });
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            handleToast({ type: "ERROR", message: "Failed to update approval status" });
        }
    };



    const handleDelete = async (quotationId) => {
        const result = await confirmDelete("Are you sure?", "You won't be able to revert this!");
        if (!result.isConfirmed) return;

        try {
            await Delete("productRfqVendor", quotationId);
            handleToast({ type: "SUCCESS", message: "Quotation deleted successfully" });
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            handleToast({ type: "ERROR", message: "Failed to delete quotation" });
        }
    };

    const resetForm = () => {
        setFormData({
            quotation_number: "",
            rfq_id: "",
            vendor_id: "",
            vendor_name: "",
            vendor_email: "",
            quoted_price: "",
            delivery_time: "",
            notes: "",
            status: "",
            store: storeId,
        });
        setQuotationFile(null);
        setEditingQuotation(null);
        setErrors({});
    };


    const handleApproval = async (quotation) => {
        try {
            await Patch("productRfqVendor", quotation.id, {
                status: "approved"
            });

            handleToast({ type: "SUCCESS", message: "Quotation approved successfully" });
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            handleToast({ type: "ERROR", message: "Failed to approve quotation" });
        }
    };


    const handleSendReminder = async (rfqId, vendorId) => {
        if (!vendorId) {
            handleToast({
                type: "ERROR",
                message: "Vendor not found"
            });
            return;
        }

        const loadingKey = `${rfqId}-${vendorId}`;
        try {
            setReminderLoading(prev => ({ ...prev, [loadingKey]: true }));
            const payload = {
                vendor_ids: [vendorId]
            };

            const apiUrl = getAPI("productRfqSendReminder").replace("{id}", rfqId);
            await Post(apiUrl, payload);

            handleToast({
                type: "SUCCESS",
                message: "RFQ reminder sent successfully"
            });

            setRefreshTrigger(p => p + 1);
        } catch {
            handleToast({
                type: "ERROR",
                message: "Failed to send RFQ reminder"
            });
        } finally {
            setReminderLoading(prev => ({ ...prev, [loadingKey]: false }));
        }
    };
    const fetchRfqById = async () => {
        if (!viewRfqId) return;

        try {
            setLoading(true);

            const res = await Get("productRfq");
            const rfq = res.results.find(r => r.id === viewRfqId);

            if (!rfq) {
                throw new Error("RFQ not found");
            }

            setRfqDetails(rfq);
        } catch (err) {
            handleToast({
                type: "ERROR",
                message: "Failed to fetch RFQ details"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (showPoItemModal && viewRfqId) {
            fetchRfqById();
        }
    }, [showPoItemModal, viewRfqId]);


    useEffect(() => {
        if (showPoItemModal && viewRfqId) {
            fetchRfqById();
        }
    }, [showPoItemModal, viewRfqId]);



    const handleCreatePOWithItems = async () => {
        try {
            setConvertPoLoading(true);
            const items = rfqDetails.items.map(item => ({
                product_name: item.product_name,
                specification: item.specification,
                qty: item.qty ?? item.quantity,
                uom: item.uom
            }));

            await Post("productPurchaseOrder", {
                rfq: viewRfqId,
                rfq_vendor: selectedQuotationRow.id,
                store: storeId,
                items: items
            });

            handleToast({
                type: "SUCCESS",
                message: "Purchase Order created successfully"
            });

            setShowPoItemModal(false);
            setRefreshTrigger(p => p + 1);

        } catch (err) {
            console.error(err);
            handleToast({
                type: "ERROR",
                message: "Failed to create Purchase Order"
            });
        } finally {
            setConvertPoLoading(false);
        }
    };

    const STATUS_PILL_CONFIG = {
        rfq_sent: {
            bg: "#f1f5f9",
            text: "#334155",
            border: "#cbd5e1",
        },
        quotation_submitted: {
            bg: "#eaf6ff",
            text: "#0d99ff",
            border: "#b6e1ff",
        },
        approved: {
            bg: "#ecfeff",
            text: "#0f766e",
            border: "#99f6e4",
        },
        po_generated: {
            bg: "#f0fdf4",
            text: "#166534",
            border: "#bbf7d0",
        },
        under_review: {
            bg: "#f8fafc",
            text: "#64748b",
            border: "#e2e8f0",
        },
        rejected: {
            bg: "#fdecec",
            text: "#e11d48",
            border: "#fda4af",
        },
    };



    const BTN = {
        reminder: {
            bg: "#fff7ed",
            text: "#ea580c",
            border: "#fed7aa",
            hover: "#ffedd5",
        },
        create: {
            bg: "#eef2ff",
            text: "#4338ca",
            border: "#c7d2fe",
            hover: "#e0e7ff",
        },
        approve: {
            bg: "#ecfeff",
            text: "#0891b2",
            border: "#bae6fd",
            hover: "#cffafe",
        },
        convert: {
            bg: "#eff6ff",
            text: "#1d4ed8",
            border: "#93c5fd",
            hover: "#dbeafe",
        },

    };

    const columns = [

        { header: "RFQ Number", field: "rfq_number" },
        { header: "Vendor", field: "vendor_name" },
        { header: "Quoted Price", render: (row) => `$${parseFloat(row.total_quoted_price || 0).toFixed(2)}` },
        { header: "Delivery Days", field: "delivery_days" },
        {
            header: "Status",
            render: (row) => {
                const statusKey = row.po_generated
                    ? "po_generated"
                    : row.status;

                const cfg = STATUS_PILL_CONFIG[statusKey];

                return (
                    <StatusPill
                        label={row.status.replace(/_/g, " ")}
                        bgColor={cfg?.bg}
                        textColor={cfg?.text}
                        borderColor={cfg?.border}
                    />
                );
            }
        },
        { header: "Reminder Time", field: "reminder_count" },
        {
            header: "Last Reminder",
            render: (row) =>
                row.last_reminder_at
                    ? new Date(row.last_reminder_at).toLocaleDateString("en-GB")
                    : ""
        },
        {
            header: "Reminder",
            render: (row) => {
                const loadingKey = `${row.rfq}-${row.vendor}`;
                const isLoading = reminderLoading[loadingKey];

                return row.status === "rfq_sent" && (
                    <ActionButton
                        onClick={() => handleSendReminder(row.rfq, row.vendor)}
                        loading={isLoading}
                        bgColor={BTN.reminder.bg}
                        textColor={BTN.reminder.text}
                        borderColor={BTN.reminder.border}
                        hoverBgColor={BTN.reminder.hover}
                    >
                        Send Reminder
                    </ActionButton>
                );
            }
        }
        ,
        {
            header: "Actions",
            render: (row) => (
                <div>
                    {row.status === "rejected" ? (
                        <button
                            disabled
                            style={{
                                backgroundColor: "#e9ecef",
                                color: "#6c757d",
                                border: "1px solid #ced4da",
                                padding: "6px 14px",
                                borderRadius: "20px",
                                fontSize: "13px",
                                fontWeight: "500",
                                cursor: "not-allowed"
                            }}
                        >
                            Cancelled
                        </button>
                    ) : (
                        (row.status !== "approved" && row.status === "rfq_sent") && (

                            <ActionButton
                                onClick={() => openQuotationModal(row)}
                                bgColor={BTN.create.bg}
                                textColor={BTN.create.text}
                                borderColor={BTN.create.border}
                                hoverBgColor={BTN.create.hover}
                            >
                                Create Quotation
                            </ActionButton>
                        )
                    )}
                    {row.status === "quotation_submitted" && (
                        <ActionButton
                            onClick={() => handleApproval(row)}
                            bgColor={BTN.approve.bg}
                            textColor={BTN.approve.text}
                            borderColor={BTN.approve.border}
                            hoverBgColor={BTN.approve.hover}
                        >
                            Approve
                        </ActionButton>

                    )}
                    {row.status === "approved" && (
                        row.po_generated ? (
                            <ActionButton
                                bgColor="#eef2ff"
                                textColor="#3730a3"
                                borderColor="#c7d2fe"
                                hoverBgColor="#eef2ff"
                                style={{
                                    cursor: "default",
                                    pointerEvents: "none",
                                }}
                            >
                                PO Generated
                            </ActionButton>
                        ) : (
                            <ActionButton
                                onClick={() => {
                                    setSelectedQuotationRow(row);
                                    setViewRfqId(row.rfq);
                                    setShowPoItemModal(true);
                                }}
                                bgColor={BTN.convert.bg}
                                textColor={BTN.convert.text}
                                borderColor={BTN.convert.border}
                                hoverBgColor={BTN.convert.hover}
                            >
                                Convert to PO
                            </ActionButton>
                        )
                    )}
                </div>
            )
        },
    ];
    return (
        <div className="responsive-box">
            <div className="inventory-header">
                <div className="inventory-title">Quotation Management</div>
                <ReusableModal
                    title={`${editingQuotation ? "Add" : "Add"} Quotation`}
                    show={showModal}
                    handleClose={() => {
                        setShowModal(false);
                        setIsCreatingFromTable(false);
                        resetForm();
                    }}
                    width="800px"
                    height="500px"
                >
                    <form onSubmit={handleSubmit}>
                        <div className="d-flex flex-column mx-3">
                            {!isCreatingFromTable && (
                                <>
                                    <div className="d-flex align-items-start mb-4 mx-3">
                                        <div className="d-flex flex-column" style={{ width: "450px" }}>
                                            <PaginatedSearchSelect
                                                api="rfq"
                                                GetPaginatedData={GetPaginatedData}
                                                getOptionLabel={(opt) =>
                                                    opt.rfq_name
                                                        ? `${opt.rfq_name} - ${opt.rfq_number}`
                                                        : opt.rfq_number
                                                }
                                                onSelect={(opt) => {
                                                    if (!opt) return;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        rfq_id: opt.id,
                                                        rfq_number: opt.rfq_number
                                                    }));
                                                    if (errors.rfq_number) {
                                                        setErrors(prev => ({ ...prev, rfq_number: "" }));
                                                    }
                                                }}
                                                placeholder="Select RFQ *"
                                                value={
                                                    formData.rfq_id
                                                        ? {
                                                            id: formData.rfq_id,
                                                            rfq_number: formData.rfq_number
                                                        }
                                                        : null
                                                }
                                                required
                                                error={errors.rfq_number}
                                                style={{ width: "340px" }}
                                            />
                                        </div>
                                        <PaginatedSearchSelect
                                            api="vendorDepartmentData"
                                            GetPaginatedData={GetPaginatedData}
                                            getOptionLabel={(opt) => opt.name}
                                            onSelect={(opt) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    vendor_id: opt?.id || "",
                                                    vendor_name: opt?.name || ""
                                                }));
                                                if (errors.vendor_id) setErrors(prev => ({ ...prev, vendor_id: "" }));
                                            }}
                                            placeholder="Select Vendor *"
                                            value={formData.vendor_id ? { id: formData.vendor_id, name: formData.vendor_name } : null}
                                            required
                                            error={errors.vendor_id}
                                            filterFn={(vendor) => vendor.type === "purchase" && !vendor.is_deleted}
                                            style={{ width: "500px" }}
                                        />
                                    </div>

                                </>
                            )}
                            <div className="d-flex align-items-center  mx-3">
                                <ReusableInput
                                    id="quoted_price"
                                    type="number"
                                    name="quoted_price"
                                    label="Quoted Price"
                                    value={formData.quoted_price}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    required
                                    error={errors.quoted_price}
                                />
                                <ReusableInput
                                    id="delivery_time"
                                    type="text"
                                    name="delivery_time"
                                    label="Delivery Days"
                                    value={formData.delivery_time}
                                    onChange={handleChange}
                                    placeholder="e.g., 2-4"
                                />
                            </div>
                            <div className="mb-3">
                                <textarea
                                    id="notes"
                                    name="notes"
                                    placeholder="Additional Notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="3"
                                    style={{
                                        width: "100%",
                                        padding: "8px",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px"
                                    }}
                                />
                            </div>
                            <ReusableInput
                                type="file"
                                name="upload_pdf_for_referance"
                                label="Upload PDF Reference"
                                accept=".pdf"
                                onChange={handleChange}
                            />

                            <div className="d-flex justify-content-end mt-3">
                                <RegularButton
                                    id="cancelBtn"
                                    text="Cancel"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    backgroundColor="#ffffff"
                                    color="#282E26"
                                />
                                <RegularButton
                                    id="saveBtn"
                                    text={editingQuotation ? "Save" : "Save"}
                                    type="submit"
                                    backgroundColor="#282e26"
                                />
                            </div>
                        </div>
                    </form>
                </ReusableModal>

                <ReusableModal
                    title="Quotation Approval"
                    show={showApprovalModal}
                    handleClose={() => { setShowApprovalModal(false); setSelectedQuotation(null); }}
                    width="600px"
                    height="400px"
                >
                    <form onSubmit={handleApprovalSubmit}>
                        <div className="d-flex flex-column mx-3">
                            {selectedQuotation && (
                                <div className="mb-3">
                                    <h6>Quotation: {selectedQuotation.quotation_number}</h6>
                                    <p>Vendor: {selectedQuotation.vendor_name}</p>
                                    <p>Quoted Price: ${parseFloat(selectedQuotation.total_quoted_price || 0).toFixed(2)}</p>
                                </div>
                            )}

                            <div className="mb-3">
                                <label>Approval Status *</label>
                                <select
                                    name="approval_status"
                                    value={approvalData.approval_status}
                                    onChange={handleApprovalChange}
                                    style={{
                                        width: "100%",
                                        padding: "8px",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px"
                                    }}
                                    required
                                >
                                    <option value="">Select Status</option>
                                    <option value="approved">Approve</option>
                                    <option value="rejected">Reject</option>
                                    <option value="under_review">Keep Under Review</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <textarea
                                    name="approval_notes"
                                    placeholder="Approval Notes"
                                    value={approvalData.approval_notes}
                                    onChange={handleApprovalChange}
                                    rows="4"
                                    style={{
                                        width: "100%",
                                        padding: "8px",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px"
                                    }}
                                />
                            </div>

                            <div className="d-flex justify-content-end mt-3">
                                <RegularButton
                                    text="Cancel"
                                    onClick={() => { setShowApprovalModal(false); setSelectedQuotation(null); }}
                                    backgroundColor="#ffffff"
                                    color="#282E26"
                                />
                                <RegularButton
                                    text="Submit"
                                    type="submit"
                                    backgroundColor="#282e26"
                                />
                            </div>
                        </div>
                    </form>
                </ReusableModal>
            </div>
            <ReusableModal
                title="Convert RFQ to Purchase Order"
                show={showPoItemModal}
                handleClose={() => setShowPoItemModal(false)}
                width="900px"
            >
                {loading && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <p>Loading RFQ details...</p>
                    </div>
                )}

                {convertPoLoading && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <p>Creating Purchase Order...</p>
                    </div>
                )}

                {rfqDetails?.items?.length > 0 ? (
                    <>
                        <table
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                marginBottom: "16px"
                            }}
                        >
                            <thead>
                                <tr>
                                    <th style={thStyle}>Product Name</th>
                                    <th style={thStyle}>Specification</th>
                                    <th style={thStyle}>Quantity</th>
                                    <th style={thStyle}>UOM</th>
                                    <th style={thStyle}>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {rfqDetails.items.map((item, index) => (
                                    <tr key={item.id || index}>
                                        <td style={tdStyle}>
                                            <input
                                                type="text"
                                                value={item.product_name}
                                                onChange={(e) => {
                                                    setRfqDetails(prev => ({
                                                        ...prev,
                                                        items: prev.items.map((it, i) =>
                                                            i === index
                                                                ? { ...it, product_name: e.target.value }
                                                                : it
                                                        )
                                                    }));
                                                }}
                                                style={{
                                                    width: "100%",
                                                    padding: "4px",
                                                    border: "1px solid #ccc"
                                                }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="text"
                                                value={item.specification}
                                                onChange={(e) => {
                                                    setRfqDetails(prev => ({
                                                        ...prev,
                                                        items: prev.items.map((it, i) =>
                                                            i === index
                                                                ? { ...it, specification: e.target.value }
                                                                : it
                                                        )
                                                    }));
                                                }}
                                                style={{
                                                    width: "100%",
                                                    padding: "4px",
                                                    border: "1px solid #ccc"
                                                }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.qty ?? item.quantity}
                                                onChange={(e) => {
                                                    const qty = Number(e.target.value);
                                                    setRfqDetails(prev => ({
                                                        ...prev,
                                                        items: prev.items.map((it, i) =>
                                                            i === index
                                                                ? { ...it, qty: qty }
                                                                : it
                                                        )
                                                    }));
                                                }}
                                                style={{
                                                    width: "80px",
                                                    padding: "4px"
                                                }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <PaginatedSearchSelect
                                                api="allUomData"
                                                GetPaginatedData={GetPaginatedData}
                                                getOptionLabel={(opt) => opt.uom_title}
                                                value={item.uom ? { id: item.uom, uom_title: item.uom_title } : null}
                                                placeholder="Select UOM"
                                                pageSize={10}
                                                onSelect={(opt) => {
                                                    setRfqDetails(prev => ({
                                                        ...prev,
                                                        items: prev.items.map((it, i) =>
                                                            i === index
                                                                ? { ...it, uom: opt?.id, uom_title: opt?.uom_title }
                                                                : it
                                                        )
                                                    }));
                                                }}
                                                style={{ width: "150px" }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <button
                                                onClick={() => {
                                                    setRfqDetails(prev => ({
                                                        ...prev,
                                                        items: prev.items.filter((_, i) => i !== index)
                                                    }));
                                                }}
                                                style={{
                                                    backgroundColor: "transparent",
                                                    border: "none",
                                                    padding: "6px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}
                                            >
                                                <FaTrash size={16} color="#dc3545" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div style={{
                            marginBottom: "16px",
                            display: "flex",
                            justifyContent: "flex-end"
                        }}>
                            <button
                                onClick={() => {
                                    setRfqDetails(prev => ({
                                        ...prev,
                                        items: [...prev.items, {
                                            id: Date.now(),
                                            product_name: "",
                                            specification: "",
                                            ordered_quantity: 1
                                        }]
                                    }));
                                }}
                                style={{
                                    backgroundColor: "#000",
                                    color: "#fff",
                                    border: "none",
                                    padding: "8px 18px",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontWeight: "600"
                                }}
                            >
                                Add Item
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p>No items found</p>
                        <div style={{ marginBottom: "16px" }}>
                            <button
                                onClick={() => {
                                    setRfqDetails(prev => ({
                                        ...prev,
                                        items: [{
                                            id: Date.now(),
                                            product_name: "",
                                            specification: "",
                                            ordered_quantity: 1
                                        }]
                                    }));
                                }}
                                style={{
                                    backgroundColor: "#28a745",
                                    color: "white",
                                    border: "none",
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    cursor: "pointer"
                                }}
                            >
                                Add Item
                            </button>
                        </div>
                    </>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <RegularButton
                        text={convertPoLoading ? "Creating..." : "Submit"}
                        onClick={handleCreatePOWithItems}
                        backgroundColor={convertPoLoading ? "#6c757d" : "#282e26"}
                        disabled={convertPoLoading}
                    />
                </div>
            </ReusableModal>
            <AssetTable
                data={quotationData}
                columns={columns}
                loading={loading}
                showAction={true}
                // onEdit={handleEdit}
                onDelete={handleDelete}
                showFooter
                currentPage={currentPage}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
                searchTerm={searchTerm}
                onSearch={setSearchTerm}
            />
        </div>
    );
};

export default InventoryQuotation
